/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { sanitizeHtml } from "../utils/sanitize";
import styled from "styled-components";
import Stack from "../components/ui/Stack";
import Title from "../components/ui/Title";
import Text from "../components/ui/Text";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import { ROUTES, BOOKING_STATUS, CART_LINE_TYPES } from "../constants/constants";
import { queryKeys } from "../lib/react-query/queryKeys";
import { useStepManager } from "../hooks/useStepManager";
import Button from "../components/ui/Button";
import ButtonGroup from "../components/ui/ButtonGroup";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useWidgetCallbackParams from "../hooks/useWidgetCallbackParams";
import LoadingIndicator from "../components/LoadingIndicator";
import { useToast } from "../components/ui/Toast";
import { useCartItemStatusPage } from "../hooks/useCartItemStatusPage";
import { useInvoiceCalculations } from "../hooks/checkout/useInvoiceCalculations";
import { useConfig } from "../lib/react-query/useConfig";
import OrderDetails from "../components/booking/OrderDetails";
import InvoicePackageInstances from "../components/booking/checkout/InvoicePackageInstances";
import getStatusConfig from "../utils/getStatusConfig";
import { useDispatch } from "react-redux";
import { resetFlow, setFlow, FlowType } from "../store/slices/flowSlice";
import { setInvoice } from "../store/slices/cartSlice";
import { setSelectedServices, clearScrollToServiceId } from "../store/slices/bookingSlice";
import FormatDateTime from "../components/formatters/FormatDateTime";
import FormatDatePeriod from "../components/formatters/FormatDatePeriod";
import { extractErrorMessage } from "../utils/api/apiErrors";
import { canRetryPayment } from "../utils/helpers/invoice";

const StyledProductStatus = styled(Stack)`
  place-items: center;
  margin-top: 48px;
`;

const ProductStatusInfo = styled(Stack)`
  place-items: center;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  text-align: center;
`;

const DeliveryDWrapper = styled.div`
  padding-top: 24px;
  margin-top: 32px;
  width: 100%;
  border-top: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
`;

const BookingDateLabelsWrapper = styled(Stack)`
  place-items: center;
  width: 100%;
`;

const BookingDateLabel = styled.div`
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  text-align: center;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.1);
`;

const CartItemStatus = ({ status: initialStatus }) => {
  const { navigateToStep } = useStepManager();
  const {
    invoiceId: callbackInvoiceId,
    serviceIds,
    location: preselectLocation,
    provider: preselectProvider,
    promotionCode: preselectCoupon,
    clearParams,
  } = useWidgetCallbackParams();
  // See BookingStatus: a stored-payment-method charge has no redirect, so the
  // invoice id arrives through router state rather than the callback hash.
  const routerState = useLocation().state;
  const invoiceId = callbackInvoiceId || routerState?.invoiceId || null;
  const hadPreselects =
    serviceIds.length > 0 || !!preselectLocation || !!preselectProvider || !!preselectCoupon;
  const { data: config } = useConfig();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const seededBookingHistoryRef = useRef(false);

  const {
    isLoading,
    isError,
    error,
    invoice,
    cartEntities,
    orderStatus,
  } = useCartItemStatusPage({
    invoiceId,
    initialStatus,
  });

  const { hasRestAmountWithDeposit, price, deposit, discount, tip, taxes, hasTaxes } =
    useInvoiceCalculations(invoice);

  const statusConfig = useMemo(() => getStatusConfig(orderStatus, !!invoice, "product"), [orderStatus, invoice]);
  const deliveryDescription = cartEntities.items.some(item => item.type === "product") ? config?.product_delivery_text : null;
  const shouldContinueBooking = hadPreselects && orderStatus === BOOKING_STATUS.CONFIRMED;

  useEffect(() => {
    if (isLoading) return;
    clearParams();
    if (shouldContinueBooking) {
      dispatch(setFlow(FlowType.BOOKING));
    } else if (invoice) {
      dispatch(setFlow(FlowType.CART));
    } else {
      dispatch(resetFlow());
    }
  }, [clearParams, isLoading, dispatch, invoice, shouldContinueBooking]);

  // The boot redirect collapsed history to a single entry. Seed Home beneath
  // Services so the services-step back button (navigate(-1)) returns to the
  // booking overview, just as opening Services from Home normally would.
  useEffect(() => {
    if (!shouldContinueBooking || seededBookingHistoryRef.current) return;

    seededBookingHistoryRef.current = true;
    navigate(ROUTES.HOME, { replace: true });
    navigate(ROUTES.SERVICES);
  }, [shouldContinueBooking, navigate]);

  // On any non-success status (pay-later / cancelled / pending) clear the
  // preselected services. The membership hasn't activated, so leaving them
  // would re-open the access modal the moment the user navigates away.
  // CONFIRMED keeps the selection — see the bounce below.
  useEffect(() => {
    if (!orderStatus) return;
    if (orderStatus === BOOKING_STATUS.CONFIRMED) return;
    dispatch(setSelectedServices([]));
    dispatch(clearScrollToServiceId());
  }, [orderStatus, dispatch]);

  // A confirmed membership purchase can activate a recurring profile —
  // refresh the cache so the Buy guard sees it without a reload.
  useEffect(() => {
    if (orderStatus !== BOOKING_STATUS.CONFIRMED) return;
    if (!cartEntities.items.some((item) => item.type === CART_LINE_TYPES.MEMBERSHIP)) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.purchasedMemberships.all });
  }, [orderStatus, cartEntities.items, queryClient]);

  useEffect(() => {
    if (isError && error?.response?.data?.message) {
      toast({
        title: "Error",
        description: `There was an error loading your order. Reason: ${extractErrorMessage(error)}`,
        variant: "error",
      });
    }
  }, [isError, error, toast]);

  const handlePayNow = () => {
    dispatch(setFlow(FlowType.CART));
    dispatch(setInvoice(invoice));
    navigateToStep(ROUTES.CHECKOUT);
  };

  const handleContinueShopping = () => {
    navigateToStep(ROUTES.HOME);
  };

  if (isError) {
    return <Navigate replace to={ROUTES.HOME} />;
  }

  // shouldContinueBooking: membership unlocked the service — the effect above
  // redirects into the booking flow at the services step.
  if (isLoading || !orderStatus || shouldContinueBooking) {
    return (
      <Container>
        <NavigationHeader />
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader navigation cart clientLogin />
      <StyledProductStatus $gap="1.5rem">
        <ProductStatusInfo $gap="0.5rem">
          {statusConfig.icon}
          <Title size="h3">{statusConfig.title}</Title>
          {statusConfig.message && <Text color="dark">{statusConfig.message}</Text>}

          {orderStatus === BOOKING_STATUS.DELAY && invoice?.due_datetime && (
            <BookingDateLabelsWrapper $gap="0.25rem">
              <BookingDateLabel>
                <Text color="dark">
                  <FormatDateTime value={invoice.due_datetime} format={FormatDateTime.FORMAT.DATE} />,{" "}
                  <FormatDateTime value={invoice.due_datetime} format={FormatDatePeriod.FORMAT.TIME} />
                </Text>
              </BookingDateLabel>
            </BookingDateLabelsWrapper>
          )}
        </ProductStatusInfo>

        {cartEntities.items.length > 0 && (
          <OrderDetails
            invoice={invoice}
            items={cartEntities.items}
            currency={cartEntities.currency}
            price={price}
            tip={tip}
            deposit={hasRestAmountWithDeposit ? deposit : null}
            discount={discount}
            taxes={hasTaxes ? taxes : null}
          />
        )}

        <InvoicePackageInstances invoice={invoice} />

        <ButtonGroup displayAs="column" itemsWidth="full">
          {invoice && (orderStatus === BOOKING_STATUS.DELAY || canRetryPayment(invoice)) && (
            <Button
              variant="primary"
              title="Pay now"
              fullWidth
              onClick={handlePayNow}
            />
          )}
          <Button
            variant="secondary"
            title="Continue shopping"
            fullWidth
            onClick={handleContinueShopping}
          />
        </ButtonGroup>

        {deliveryDescription && (
          <DeliveryDWrapper>
            <Title size="h3">Delivery</Title>
            <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(deliveryDescription) }} />
          </DeliveryDWrapper>
        )}

      </StyledProductStatus>
    </Container>
  );
};

export default CartItemStatus;